import React, { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { 
  ChevronRight, 
  Activity, 
  CheckCircle, 
  AlertTriangle, 
  XCircle, 
  Server, 
  Database,
  Globe,
  CreditCard,
  Search
} from "lucide-react";

const AdminHealthPage = () => {
  const [healthStatus, setHealthStatus] = useState({
    fcAnalises: { status: 'loading', url: '', label: 'FC Análises' },
    asaas: { status: 'loading', url: '', label: 'Asaas' },
    environment: { status: 'loading', value: '', label: 'Ambiente React' },
    database: { status: 'loading', value: '', label: 'Banco de Dados (Firebase)' }
  });

  useEffect(() => {
    const envReact = process.env.REACT_APP_ENV || process.env.NODE_ENV || "Não definido";
    const normalizedEnv = envReact.toLowerCase();

    const isProduction = normalizedEnv === 'production';
    const isTestEnvironment =
      normalizedEnv === 'development' ||
      normalizedEnv === 'homolog' ||
      normalizedEnv === 'test';

    const classifyEndpoint = (url, sandboxMarkers, productionMarker) => {
      const normalizedUrl = (url || "").toLowerCase();

      if (!normalizedUrl) return 'error';

      const isSandbox = sandboxMarkers.some(marker => normalizedUrl.includes(marker));
      const isProductionUrl = normalizedUrl.includes(productionMarker);

      if (isProduction) {
        if (isSandbox) return 'error';
        return isProductionUrl ? 'success' : 'error';
      }

      if (isTestEnvironment) {
        return isSandbox ? 'warning' : 'error';
      }

      return 'error';
    };

    // 1. Diagnóstico da variável frontend FC Análises
    const fcUrl = process.env.REACT_APP_FC_BASE_URL || "";
    const fcStatus = classifyEndpoint(
      fcUrl,
      ["stage-api", "sandbox", "homolog", "test"],
      "api.fichacertadigital.com.br"
    );

    // 2. Diagnóstico da variável frontend Asaas
    const asaasUrl = process.env.REACT_APP_ASAAS_API_URL || "";
    const asaasStatus = classifyEndpoint(
      asaasUrl,
      ["sandbox", "test"],
      "api.asaas.com"
    );

    // 3. Ambiente React
    let envStatus = 'error';
    if (isTestEnvironment) {
      envStatus = 'warning';
    } else if (isProduction) {
      envStatus = 'success';
    }

    // 4. Leitura Banco de Dados (Firebase) com Trava de Emulador
    const dbId = process.env.REACT_APP_FIREBASE_DATABASE_ID || "Não definido";
    const isEmulator = process.env.REACT_APP_USE_EMULATOR === 'true';
    
    let dbStatus = 'error';
    let dbDisplayValue = dbId;

    if (isEmulator) {
      dbStatus = 'warning';
      dbDisplayValue = `${dbId} (Emulador Local)`;
    } else if (dbId.toLowerCase() === 'homolog' || dbId.toLowerCase() === 'test') {
      dbStatus = 'warning';
    } else if (dbId.toLowerCase() === '(default)' || dbId.toLowerCase() === 'production') {
      dbStatus = 'success';
    }

    setHealthStatus({
      fcAnalises: { status: fcStatus, url: fcUrl || "VAZIO", label: 'Motor FC Análises' },
      asaas: { status: asaasStatus, url: asaasUrl || "VAZIO", label: 'Gateway Asaas' },
      environment: { status: envStatus, value: envReact, label: 'Ambiente da Aplicação' },
      database: { status: dbStatus, value: dbDisplayValue, label: 'Instância Firestore' }
    });
  }, []);

  const StatusIcon = ({ status }) => {
    switch(status) {
      case 'success': return <CheckCircle className="w-6 h-6 text-green-500" />;
      case 'warning': return <AlertTriangle className="w-6 h-6 text-yellow-500" />;
      case 'error': return <XCircle className="w-6 h-6 text-red-500" />;
      default: return <Activity className="w-6 h-6 text-slate-400 animate-pulse" />;
    }
  };

  const StatusBadge = ({ status, isDatabase, value }) => {
    if (status === 'success') {
      return <span className="px-2.5 py-1 text-xs font-bold text-green-700 bg-green-100 border border-green-200 rounded-lg uppercase tracking-wider">Produção</span>;
    }
    if (status === 'warning') {
      if (isDatabase && value && value.includes("Emulador")) {
        return <span className="px-2.5 py-1 text-xs font-bold text-yellow-700 bg-yellow-100 border border-yellow-200 rounded-lg uppercase tracking-wider">Local / Emulador</span>;
      }
      return <span className="px-2.5 py-1 text-xs font-bold text-yellow-700 bg-yellow-100 border border-yellow-200 rounded-lg uppercase tracking-wider">Sandbox / Teste</span>;
    }
    if (status === 'error') {
      return <span className="px-2.5 py-1 text-xs font-bold text-red-700 bg-red-100 border border-red-200 rounded-lg uppercase tracking-wider">Configuração incompatível</span>;
    }
    return null;
  };

  const HealthCard = ({ title, icon: Icon, data, isDatabase }) => (
    <div className={`bg-white rounded-2xl border-l-4 shadow-sm p-5 transition-all
      ${data.status === 'success' ? 'border-l-green-500 border-y-slate-200 border-r-slate-200' : 
        data.status === 'warning' ? 'border-l-yellow-500 border-y-slate-200 border-r-slate-200' : 
        'border-l-red-500 border-y-slate-200 border-r-slate-200 border'}
    `}>
      <div className="flex justify-between items-start mb-4">
        <div className="flex items-center gap-3">
          <div className={`p-2 rounded-xl 
            ${data.status === 'success' ? 'bg-green-50 text-green-600' : 
              data.status === 'warning' ? 'bg-yellow-50 text-yellow-600' : 'bg-red-50 text-red-600'}
          `}>
            <Icon className="w-5 h-5" />
          </div>
          <h3 className="font-bold text-slate-800">{title}</h3>
        </div>
        <StatusIcon status={data.status} />
      </div>
      
      <div className="bg-slate-50 rounded-xl p-3 border border-slate-100 mt-2">
        <p className="text-[11px] font-bold text-slate-400 uppercase tracking-widest mb-1">Apontamento Atual</p>
        <p className="text-sm font-semibold text-slate-700 break-all">{data.url || data.value}</p>
      </div>

      <div className="mt-4 flex justify-between items-center">
        <span className="text-xs font-medium text-slate-500">Status Operacional:</span>
        <StatusBadge status={data.status} isDatabase={isDatabase} value={data.value} />
      </div>
    </div>
  );

  return (
    <div className="p-4 sm:p-5 xl:p-8 space-y-5 xl:space-y-6 animate-fade-in bg-slate-50 min-h-full font-sans">
      
      {/* BREADCRUMB */}
      <div className="flex items-center text-sm text-slate-500 font-medium mb-4">
        <Link to="/super-admin/dashboard" className="hover:text-brand-primary transition-colors">Dashboard</Link>
        <ChevronRight className="w-4 h-4 mx-1" />
        <span className="font-semibold text-slate-800">Saúde do Sistema</span>
      </div>

      <div className="flex items-center justify-between mb-2">
        <div>
          <h1 className="text-2xl font-bold text-slate-800 flex items-center gap-2">
            <Activity className="w-6 h-6 text-brand-primary" /> Diagnóstico de Conexões
          </h1>
          <p className="text-sm text-slate-500 font-medium mt-1">
            Diagnóstico das variáveis compiladas nesta interface.
          </p>
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-2 gap-5 xl:gap-6 mt-6">
        <HealthCard title={healthStatus.fcAnalises.label} icon={Search} data={healthStatus.fcAnalises} />
        <HealthCard title={healthStatus.asaas.label} icon={CreditCard} data={healthStatus.asaas} />
        <HealthCard title={healthStatus.environment.label} icon={Globe} data={healthStatus.environment} />
        <HealthCard title={healthStatus.database.label} icon={Database} data={healthStatus.database} isDatabase={true} />
      </div>

      <div className="mt-8 bg-blue-50 border border-blue-200 rounded-2xl p-5">
        <div className="flex items-start gap-3">
          <Server className="w-5 h-5 text-blue-600 shrink-0 mt-0.5" />
          <div>
            <h4 className="font-bold text-blue-900 text-sm">O que isso significa?</h4>
            <p className="text-sm text-blue-800 mt-1 font-medium leading-relaxed">
              Esta tela não executa health-check das APIs externas. Ela lê as <strong>variáveis de ambiente compiladas</strong> no build desta interface React e verifica se são coerentes com o ambiente selecionado.
              <br/><br/>
              As integrações FC Análises e Asaas são executadas pelo backend. Aqui, seus apontamentos servem apenas como diagnóstico da configuração compilada do frontend.
            </p>
          </div>
        </div>
      </div>

    </div>
  );
};

export default AdminHealthPage;